(ns common.schema.entities.pipeline
  (:require
   [clojure.set :as set]
   [common.schema.assets :as assets]
   [common.schema.constraints :as constraints]
   [common.schema.entity-type-names :as entity-type-names]
   [common.schema.option-filters :as option-filters]
   [common.schema.schema-utils :as su]))

(def entity-type-mappings
  {:entity.type/oppo
   {:pipeline-key             :oppo/pipeline
    :pipeline-type            :deal-flow-pipelines
    :settings-page            :settings/deal-flow-pipelines
    :pipeline-url             "dealflow"
    :stage-entity-type        :entity.type/stage
    :stage-attribute          :oppo/stage
    :stage-name-attribute     :stage/name
    :stage-pipeline-attribute :stage/pipeline
    :target-attribute         :oppo/target-asset}
   :entity.type/fundraising-opportunity
   {:pipeline-key             :fundraising-opportunity/pipeline
    :pipeline-type            :fundraising-pipelines
    :settings-page            :settings/fundraising-pipelines
    :pipeline-url             "fundraising"
    :stage-entity-type        :entity.type/opportunity-stage
    :stage-attribute          :fundraising-opportunity/opportunity-stage
    :stage-name-attribute     :opportunity-stage/name
    :stage-pipeline-attribute :opportunity-stage/pipeline
    :target-attribute         :fundraising-opportunity/investor}
   :entity.type/sales-opportunity
   {:pipeline-key             :sales-opportunity/pipeline
    :pipeline-type            :sales-pipelines
    :settings-page            :settings/sales-pipelines
    :pipeline-url             "sales"
    :stage-entity-type        :entity.type/sales-opportunity-stage
    :stage-attribute          :sales-opportunity/stage
    :stage-name-attribute     :sales-opportunity-stage/name
    :stage-pipeline-attribute :sales-opportunity-stage/pipeline
    :target-attribute         :sales-opportunity/customer}
   :entity.type/exit-opportunity
   {:pipeline-key             :exit-opportunity/pipeline
    :pipeline-type            :exit-pipelines
    :settings-page            :settings/exit-pipelines
    :pipeline-url             "exit-opportunities"
    :stage-entity-type        :entity.type/exit-opportunity-stage
    :stage-attribute          :exit-opportunity/stage
    :stage-name-attribute     :exit-opportunity-stage/name
    :stage-pipeline-attribute :exit-opportunity-stage/pipeline
    :target-attribute         :exit-opportunity/buyer}})

(def entity-type-to-stage-mappings
  {:entity.type/oppo                    :entity.type/stage
   :entity.type/fundraising-opportunity :entity.type/opportunity-stage
   :entity.type/sales-opportunity       :entity.type/sales-opportunity-stage
   :entity.type/exit-opportunity        :entity.type/exit-opportunity-stage})

(defn- pipeline-target-types
  [pipeline default-target-types]
  (vec (or (seq (:pipeline/target-asset-types pipeline))
           default-target-types)))

(defn associated-entity-field->entity-types
  [{:keys [entity-type pipeline]}]
  (if-not pipeline
    {}
    (case entity-type
      :entity.type/oppo
      {:oppo/target-asset (pipeline-target-types pipeline
                                                 (:entity.type/oppo assets/target-asset-type-mappings))}

      :entity.type/fundraising-opportunity
      {:fundraising-opportunity/investor [:entity.type/investor]}

      :entity.type/sales-opportunity
      {:sales-opportunity/customer (pipeline-target-types pipeline
                                                          (:entity.type/sales-opportunity
                                                           assets/target-asset-type-mappings))}

      :entity.type/exit-opportunity
      {:exit-opportunity/buyer [:entity.type/investor]}

      {})))

(defn entity-type->stage-name
  [entity-type]
  (case entity-type
    :entity.type/oppo :stage/name
    :entity.type/sales-opportunity :sales-opportunity-stage/name
    :entity.type/fundraising-opportunity :opportunity-stage/name
    :entity.type/exit-opportunity :exit-opportunity-stage/name
    :stage/name))

(def schema
  (vec
   (concat
    [[:pipeline/name
      {:field/data-type             :data.type/text-simple
       :field/mandatory             :mandatory/always
       :field/name-attribute        :pipeline/name
       :field/system-required?      true
       :field/system-semantics      su/user-provided-system-required-field-semantics

       :field/lower-case-str?       true
       :field/constraints           {:constraint/mandatory-fn      (fn [field-value _entity-data]
                                                                     (empty? (str field-value)))
                                     :constraint/string-min-length 1
                                     :constraint/unique-tenant-ignore-case
                                     {:validation-fn (partial constraints/tenant-unique-ignore-case
                                                              :pipeline/name
                                                              [:entity.type/pipeline])}}

       :field/default-visibility    {:editor           :editor.type/text-simple
                                     :grid             :editor.type/text-simple
                                     :filter           :filter.type/text
                                     :field-visibility :in-use-disabled}
       :field/default-authorization {:access/read  :role/user
                                     :access/write :role/admin}
       :field/not-clearable?        true
       :field/translations          {:en "Pipeline name"}}]
     [:pipeline/entity-type
      {:field/data-type             :data.type/keyword
       :field/default-visibility    {:editor :editor.type/none
                                     :grid   :editor.type/none
                                     :filter :filter.type/text
                                     :field-visibility :in-use-disabled}
       :field/default-authorization {:access/read  :role/user
                                     :access/write :role/admin}
       :field/translations          {:en "Entity type"}}]

     [:pipeline/teams
      {:field/data-type             :data.type/ref-multi
       :value-type                  :entity
       :field/entity-types          [:entity.type/team]
       :field/default-authorization {:access/read  :role/user
                                     :access/write :role/admin}
       :field/not-importable?       true
       :field/default-visibility    {:editor :editor.type/none
                                     :grid   :editor.type/multi-select
                                     :filter :filter.type/select
                                     :field-visibility :in-use-disabled}
       :field/translations          {:en "Teams"}}]
     [:pipeline/target-asset-types
      {:field/data-type             :data.type/keyword-multi
       :value-type                  :keyword
       :field/default-visibility    {:editor :editor.type/multi-select
                                     :grid   :editor.type/multi-select
                                     :filter :filter.type/select
                                     :field-visibility :in-use-disabled}
       :field/default-authorization {:access/read  :role/user
                                     :access/write :role/user}
       :field/constraints           {:constraint/investor-or-person-company
                                     {:validation-fn (partial
                                                      constraints/investor-or-person-company
                                                      :pipeline/target-asset-types
                                                      nil)}}
       :field/in-use-for-this-data? (fn [pipeline-entity
                                         _entity-types-lookup
                                         _has-feature-access-fn
                                         _has-entity-access?]
                                      (not-empty (get assets/target-asset-type-mappings
                                                      (:pipeline/entity-type pipeline-entity))))
       :options                     (->> assets/target-asset-type-mappings
                                         (map (fn [[_et asset-types]]
                                                asset-types))
                                         (flatten)
                                         (distinct)
                                         (remove nil?)
                                         (map (fn [at]
                                                {:key   at
                                                 :label (->> entity-type-names/entities
                                                             at
                                                             :field/translations
                                                             :en)}))
                                         (vec))
       :field/option-filter-fn      (partial option-filters/asset-types
                                             (fn [pipeline]
                                               (when-let [target-type (:pipeline/entity-type pipeline)]
                                                 (get assets/target-asset-type-mappings target-type))))
       :field/translations          {:en "Asset types"}}]]

    (su/multi-ref-field :pipeline/possible-owner-teams
                        "Allowed owners"
                        [:entity.type/account]
                        {:option-filter-fn         option-filters/active-users
                         :default-field-visibility :in-use
                         :os-filter-terms-fn       (fn [_]
                                                     {"extras.active-user" true})})
    (su/single-ref-field :pipeline/fund
                         "Default fund"
                         [:entity.type/fund]
                         {:default-field-visibility :not-in-use
                          :field-in-use-filter su/fund-field-in-use?})
    (su/multi-ref-field :pipeline/valid-funds
                        "Valid funds"
                        [:entity.type/fund]
                        {:field-in-use-filter su/fund-field-in-use?
                         :default-field-visibility :in-use
                         :authorization       su/access-admin-user})
    (su/boolean-field :pipeline/allow-without-target?
                      "Allow creating opportunity without target?"
                      {:authorization su/access-admin-user
                       :default-field-visibility :not-in-use})
    (su/boolean-field :pipeline/inactive?
                      "Pipeline archived?"
                      {:authorization su/access-admin-user
                       :default-field-visibility :in-use-disabled})
    (su/text-edn-field :pipeline/settings
                       "Pipeline configuration"
                       {:authorization            su/access-user-user
                        :hidden-from-config?      true
                        :default-field-visibility :in-use-disabled})
    (su/text-edn-field :pipeline/creator-config
                       "Entity Creation Config edn data."
                       {:authorization su/access-admin-user
                        :default-field-visibility :in-use-disabled
                        :hidden-from-config? true})
    (su/multi-ref-field :pipeline/checklist-configurations
                        "Pipeline checklists"
                        [:entity.type/check-list]
                        {:authorization            su/access-user-user
                         :hidden-from-config?      true
                         :create-on-fly?           true
                         :editor-type              :editor.type/none
                         :grid-type                :editor.type/none
                         :default-field-visibility :in-use-disabled}))))

(def pipeline-key-by-entity-type
  (->> entity-type-mappings
       (mapv (fn [[et {:keys [pipeline-key]}]]
               [et pipeline-key]))
       (into {})))

(defn pipeline-key
  [entity-type]
  (get pipeline-key-by-entity-type entity-type))

(def pipeline-attributes
  (-> pipeline-key-by-entity-type
      vals
      set))

(defn pipeline-attribute?
  [attribute]
  (contains? pipeline-attributes attribute))

(def entity-type-by-pipeline-key
  (set/map-invert pipeline-key-by-entity-type))

(defn pipeline-entity-type-by-attribute
  [attribute]
  (get entity-type-by-pipeline-key attribute))

(defn containing-pipeline-id
  [entity]
  (when-let [pipeline-attribute (pipeline-key (:entity/type entity))]
    (when-let [pipeline (get entity pipeline-attribute)]
      (or (:db/id pipeline)
          pipeline))))

(def pipeline-url-by-entity-type
  (->> entity-type-mappings
       (mapv (fn [[et {:keys [pipeline-url]}]]
               [et pipeline-url]))
       (into {})))

(defn pipeline-url
  [entity-type]
  (get pipeline-url-by-entity-type entity-type "default"))

(def pipeline-key-by-url
  (->> entity-type-mappings
       (mapv (fn [[_et {:keys [pipeline-url pipeline-key]}]]
               [pipeline-url pipeline-key]))
       (into {})))

(defn get-pipeline-key-by-url
  [url]
  (get pipeline-key-by-url url))

(defn entity-pipeline
  [entity]
  (when-let [pipeline-kw (pipeline-key (:entity/type entity))]
    (get entity pipeline-kw)))

(def pipeline-entity->source-page
  (->> entity-type-mappings
       (mapv (fn [[et {:keys [settings-page]}]]
               [et settings-page]))
       (into {})))

(def pipeline-type->entity
  (->> entity-type-mappings
       (mapv (fn [[et {:keys [pipeline-type]}]]
               [et pipeline-type]))
       (into {})
       (set/map-invert)))

(def entity->stage-pipeline-ref
  (->> entity-type-mappings
       (mapv (fn [[et {:keys [stage-pipeline-attribute]}]]
               [et stage-pipeline-attribute]))
       (into {})))

(defn pipeline-stage-key
  [entity-type]
  (get entity->stage-pipeline-ref entity-type))

(def entity->stage-attribute
  (->> entity-type-mappings
       (mapv (fn [[et {:keys [stage-attribute]}]]
               [et stage-attribute]))
       (into {})))

(defn stage-key
  [entity-type]
  (get entity->stage-attribute entity-type))

(def entity->stage-entity-type
  (->> entity-type-mappings
       (mapv (fn [[et {:keys [stage-entity-type]}]]
               [et stage-entity-type]))
       (into {})))

(defn stage-entity-type
  [entity-type]
  (get entity->stage-entity-type entity-type))

(def stage-attributes
  (-> entity->stage-attribute
      vals
      set))

(defn stage-attribute?
  [attribute]
  (contains? stage-attributes attribute))

(def entity->target-attribute
  "Maps an opportunity entity-type to the attribute that references its
   directly-linked counterparty. For investor-target-profile flows (froppo,
   exit-oppo, sales-oppo on an investor-only pipeline) this attribute points
   to the investor (the target-profile); the actual target entity is then
   reached via `(:investor/legal-entity ...)` on that investor."
  (->> entity-type-mappings
       (mapv (fn [[et {:keys [target-attribute]}]]
               [et target-attribute]))
       (into {})))

(defn target-attribute
  [entity-type]
  (get entity->target-attribute entity-type))

(def target-attributes
  (-> entity->target-attribute
      vals
      set))

(defn target-attribute?
  [attribute]
  (contains? target-attributes attribute))

(def pipeline-entity-types
  (->> entity-type-mappings
       keys
       set))

(defn pipeline-entity?
  [entity-type]
  (contains? pipeline-entity-types entity-type))

(defn pipeline
  "Resolves pipeline from given entity. Note return type entity vs. id
  for backend vs. frontend - prefer more explicit fu/pipeline-id for frontend"
  [entity]
  (or (:oppo/pipeline entity)
      (:fundraising-opportunity/pipeline entity)
      (:sales-opportunity/pipeline entity)
      (:exit-opportunity/pipeline entity)))
